import {
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { jsPDF } from "jspdf";
import { useEffect, useRef, useState } from "react";
import autoTable, { CellHookData, RowInput, UserOptions } from "jspdf-autotable";
import api from "src/store/features/api";
import { isArray } from "lodash";
import html2canvas from "html2canvas";
import { Phone } from "@mui/icons-material";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import {
  disclaimerMessage,
  formatTableHeader,
  formatTableTitle,
} from "src/components/reports/utils/ReportTools";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import CnbLogoMobile from "src/icons/CnbLogoMobile";
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import { TransactionsSummaryDeskTopStyles } from "src/components/transactions/styles/TransactionsSummaryDeskTopStyles.styles";
import { trackEvent } from "src/Amplitude";
import { getUserTimeZone } from "src/utils/hooks/useTimeZone";
import { format } from "date-fns";

const Reports = (props: {
  startDate: number | undefined;
  endDate: number | undefined;
  setErrorMessage?: React.Dispatch<React.SetStateAction<string | undefined>>;
}) => {
  const headerRef = useRef<any>();
  const footerRef = useRef<any>();
  const [isLoading, setIsloading] = useState(false);
  const { data: userData, isSuccess: userDataLoaded } = api.useGetUserDatav2Query();
  const { data, isSuccess, isFetching, isError, error, refetch } = api.useCreateAccountReportQuery({
    timezone: getUserTimeZone,
    endDate: props.endDate,
    startDate: props.startDate,
  });
  if (isError && props.setErrorMessage) {
    let message = error.message?.includes("No holdings") ? error.message : "Something went wrong";
    props.setErrorMessage(message);
  }
  useEffect(() => {
    if (!props.startDate) refetch();
  }, [props.startDate]);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const classes = TransactionsSummaryDeskTopStyles();

  // Theme
  const theme = useTheme();

  // PDF Generation Main function
  const downloadAccountSummary = async () => {
    let pdf = new jsPDF();
    // Creating header and footer from JSX
    if (isSuccess) {
      setIsloading(true);

      headerRef.current.style.display = "flex";
      footerRef.current.style.display = "flex";

      const canvas = await Promise.all([
        html2canvas(headerRef.current),
        html2canvas(footerRef.current),
      ]);

      const imageUrlHeader = canvas[0].toDataURL("image/png");
      const imageUrlFooter = canvas[1].toDataURL("image/png");

      const elements: any[] = [];
      const titles: string[] = [];
      const rowColor = ["#E5E7E8", "#FCF6EB"];
      const imgPropertiesHeader = pdf.getImageProperties(imageUrlHeader);
      const imgPropertiesFooter = pdf.getImageProperties(imageUrlFooter);

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgPropertiesHeader.height * pdfWidth) / imgPropertiesHeader.width;

      const pdfHeightFooter = (imgPropertiesFooter.height * pdfWidth) / imgPropertiesFooter.width;

      // Spliting Title and content
      for (const [title, titleData] of Object.entries(data)) {
        if (isArray(titleData) && titleData.length > 0) {
          titles.push(title);
          elements.push(titleData);
        } else {
          // Add empty table if returned data is empty
          if (
            !title.includes("accountName") &&
            !title.includes("totalPortfolioValue") &&
            !title.includes("defaultCurrency")
          ) {
            titles.push(title);
            elements.push([{ "No data": "" }]);
          }
        }
      }

      const headerPadding = 20;
      const titlePadding = 10;

      const titleYPosition = pdfHeight + headerPadding;
      const tableStartYPosition = titleYPosition + titlePadding;

      // Generating PDF from Raw data
      elements.forEach((element, iterator) => {
        if (isArray(element) && element.length > 0) {
          let headers: string[];
          if (Object.hasOwn(element[0], "lines")) {
            const { lines, ...rest } = element[0] as any;
            const lineHeaders: string[] = Object.keys(lines[0]);
            const restHeaders: string[] = Object.keys(rest);

            headers = restHeaders.concat(lineHeaders);
          } else {
            headers = Object.keys(element[0]);
          }
          const tableHeader = [headers].map((el) => {
            return formatTableHeader(el);
          });

          const formattedTitles = titles.map((el) => formatTableTitle(el));
          const tableBody: RowInput[] = [];
          element.forEach((x) => {
            if (Object.hasOwn(x, "lines")) {
              const { lines, ...rest } = x;
              const restValues: string[] = Object.values(rest);

              lines.forEach((line: any) => {
                const lineValues: string[] = Object.values(line);
                const values = restValues.concat(lineValues);
                tableBody.push(values);
              });

              // Add an empty line after processing an order
              tableBody.push([]);
            } else {
              tableBody.push(Object.values(x));
            }
          });

          const table: UserOptions = {
            head: tableHeader,
            body: tableBody,
            theme: "grid",
            startY: tableStartYPosition,
            rowPageBreak: "auto",
            bodyStyles: { valign: "top", fontSize: 7 },
            headStyles: {
              fillColor: theme.palette.primary.main,
              fontSize: 7,
            },
            margin: {
              // keep continuation pages clear of the (re-drawn) header, whose
              // height grows when a long account name wraps onto multiple lines
              top: pdfHeight + titlePadding,
              bottom: pdfHeightFooter + 20,
            },
            didDrawPage: function (HookData) {
              // Header on all pages
              pdf.addImage(imageUrlHeader, "PNG", 0, 0, pdfWidth, pdfHeight);

              // Footer on all page pages
              pdf.setFontSize(10);
              pdf.addImage(imageUrlFooter, "PNG", 0, 282, pdfWidth, pdfHeightFooter);
            },
            didParseCell: function (HookData: CellHookData) {
              if (HookData.section !== "head") {
                HookData.cell.styles.fillColor = rowColor[1];
              }
              // deposits can have really long txIds, which will compress the other columns and make the cell content look weird.
              // so we set a minimum width for the deposits table so that the non-txId columns are not compressed
              if (titles[iterator].toLowerCase().includes("deposits")) {
                HookData.cell.styles.minCellWidth = 20;
              }
              if (HookData.cell.text.toString() === "") {
                HookData.cell.styles.fillColor = "#F8F9F9";
                HookData.cell.styles.cellPadding = 0.1;
              }
            },
          };
          pdf.setTextColor(theme.palette.primary.main);
          pdf.text(formattedTitles[iterator], 15, titleYPosition);
          autoTable(pdf, table);
          pdf.addPage();
        }
      });
      pdf.setFontSize(12);
      pdf.text(disclaimerMessage, 20, 20);
      setIsloading(false);
      // DATE_TIME_Caleb&Brown_AccountSummary_CUSTOMER_ID
      let date = format(new Date(), "EEE-MMM-dd-yyyy"); // E.G Tue-Feb-06-2024
      let time = format(new Date(), "HH-mm-ss"); // E.G 10-01-38
      pdf.save(`${date}_${time}_Caleb&Brown_AccountSummary_${userData?.details.customerId ?? ""}`);
      trackEvent("Download Account Summary PDF", { date, time }, "Export Report");
    } else {
      console.log("Error: Download Account Summary PDF " + error);
    }
  };

  const Header = (props: {
    title: string;
    total: number;
    userDefaultCurrency: string;
    startDate: number | undefined;
    endDate: number | undefined;
  }) => {
    return (
      <>
        <Grid
          container
          ref={headerRef}
          minWidth={"1044px"}
          position="relative"
          sx={{ display: "none" }}>
          <Grid
            item
            xs={4}
            sx={{
              background: theme.palette.primary.main,
              border: "none",
            }}>
            <div
              style={{
                margin: "12px 12px 10px 12px",
                border: "none",
              }}>
              {/* <CnbLogoMobile color={"#F8F9F9"} width={35}  */}
            </div>
          </Grid>
          <Grid
            item
            xs={4}
            py={4}
            sx={{
              background: theme.palette.primary.main,
              alignItems: "center",
            }}>
            <CalebAndBrownLogo height={56} />
          </Grid>
          <Grid
            item
            xs={4}
            sx={{
              background: theme.palette.primary.main,
              textAlign: "end",
            }}>
            <Typography
              alignSelf="self-start"
              sx={{
                color: "#F8F9F9",
                position: "absolute",
                fontSize: "18px",
                right: "23px",
                bottom: "120.3px",
                marginRight: "45px",
              }}>
              Account Summary
            </Typography>
          </Grid>
          <Grid item xs={6} textAlign="start" py={2.5} px={8}>
            <Typography
              style={{
                fontWeight: "bold",
                fontSize: "18px",
                marginLeft: "7px",
              }}>
              {props.title}
            </Typography>
            <Typography style={{ marginLeft: "7px" }}>Timezone: {userTimeZone}</Typography>
          </Grid>
          <Grid item xs={6} textAlign="end" py={2.5} px={8}>
            <Typography
              component="div"
              style={{
                fontWeight: "bold",
                fontSize: "18px",
              }}>
              Total Holdings: {formatCurrency(props.total, props.userDefaultCurrency)}
            </Typography>
            <Typography>
              Report from
              {props.startDate
                ? ` ${dateFormatter(new Date(props.startDate), true)}`
                : ` 1st Oct 2020 `}
            </Typography>
            <Typography>
              to{" "}
              {props.endDate
                ? ` ${dateFormatter(new Date(props.endDate), true)}`
                : dateFormatter(new Date(), true)}
            </Typography>
          </Grid>
        </Grid>
      </>
    );
  };

  const Footer = () => {
    return (
      <>
        <Grid
          container
          minWidth={"1044px"}
          ref={footerRef}
          sx={{
            background: theme.palette.primary.main,
            position: "relative",
            display: "none",
          }}>
          <Grid item xs={1} p={2} sx={{ marginLeft: "55px", border: "none" }}>
            <CnbLogoMobile color={"#F8F9F9"} width={35} />
          </Grid>
          <div
            style={{
              position: "absolute",
              borderLeft: "2px solid #F8F9F9",
              height: "80%",
              top: "5px",
              left: "130px",
            }}></div>
          <Grid item sx={{ position: "relative", top: "15px", left: "10px" }}>
            <Stack>
              <Typography color={"#F8F9F9"}>{userData?.broker.name}</Typography>
              <Typography color={"#F8F9F9"}>{userData?.broker.email}</Typography>
            </Stack>
          </Grid>
          <Grid
            item
            p={2}
            sx={{
              position: "absolute",
              height: "80%",
              right: "10px",
            }}>
            <Stack direction="row" sx={{ marginRight: "50px" }}>
              <Phone
                sx={{
                  fontSize: "16px",
                  marginRight: "4px",
                  color: "#F8F9F9",
                  marginTop: "3px",
                }}
              />
              <Typography sx={{ color: "#F8F9F9", marginTop: "1px" }}>
                {userData?.broker.phoneNumberAU}
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ marginRight: "50px" }}>
              <Phone
                sx={{
                  fontSize: "16px",
                  marginRight: "4px",
                  color: "#F8F9F9",
                  marginTop: "3px",
                }}
              />
              <Typography sx={{ color: "#F8F9F9", marginTop: "1px" }}>
                {userData?.broker.phoneNumberUS}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      <Button
        disableRipple
        className={classes.exportButton}
        onClick={downloadAccountSummary}
        disabled={isFetching}>
        {isFetching ? (
          <>
            <span style={{ marginRight: "6px" }}>{"Preparing "}</span>{" "}
            <CircularProgress size={24} />
          </>
        ) : (
          "Export"
        )}
      </Button>
      {isSuccess && userDataLoaded && (
        <div>
          {isLoading ? (
            <LinearProgress />
          ) : (
            <>
              <Header
                title={data.accountName}
                total={data.totalPortfolioValue}
                userDefaultCurrency={data.defaultCurrency}
                startDate={props.startDate}
                endDate={props.endDate}
              />
              <Footer />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Reports;
